import { SubscriptionStatus } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import {
  InformationFillDuotoneIcon,
  SingleSelectCheckSolidIcon,
} from '@blocksuite/icons/rc';
import clsx from 'clsx';
import { useMemo } from 'react';

import * as styles from './styles.css';

type StatusLabelValue = SubscriptionStatus | 'expiring';

const getStatusLabel = (
  status?: SubscriptionStatus,
  canceledAt?: string | null
) => {
  // Canceled with future date = "Expiring"
  if (canceledAt && status !== SubscriptionStatus.PastDue) {
    return <StatusLabel status="expiring" />;
  }
  switch (status) {
    case SubscriptionStatus.Active:
      return <StatusLabel status={status} />;
    case SubscriptionStatus.PastDue:
      return <StatusLabel status={status} />;
    case SubscriptionStatus.Trialing:
      return <StatusLabel status={status} />;
    default:
      return null;
  }
};

export const CardNameLabelRow = ({
  cardName,
  status,
  canceledAt,
}: {
  cardName: string;
  status?: SubscriptionStatus;
  canceledAt?: string | null;
}) => {
  const statusLabel = useMemo(
    () => getStatusLabel(status, canceledAt),
    [status, canceledAt]
  );
  return (
    <div className={styles.cardNameLabelRow}>
      <div className={styles.cardName}>{cardName}</div>
      {statusLabel}
    </div>
  );
};

const StatusLabel = ({ status }: { status: StatusLabelValue }) => {
  const t = useI18n();
  const label = useMemo(() => {
    switch (status) {
      case SubscriptionStatus.Active:
        return t['com.streamlain.payment.subscription-status.active']();
      case SubscriptionStatus.PastDue:
        return t['com.streamlain.payment.subscription-status.past-due']();
      case SubscriptionStatus.Trialing:
        return t['com.streamlain.payment.subscription-status.trialing']();
      case 'expiring':
        return t['com.streamlain.payment.subscription-status.expiring']();
      default:
        return '';
    }
  }, [status, t]);

  const icon = useMemo(() => {
    switch (status) {
      case SubscriptionStatus.Active:
      case SubscriptionStatus.Trialing:
        return <SingleSelectCheckSolidIcon />;
      case SubscriptionStatus.PastDue:
      case 'expiring':
        return <InformationFillDuotoneIcon />;
      default:
        return null;
    }
  }, [status]);

  const isWarning =
    status === SubscriptionStatus.PastDue || status === 'expiring';

  return (
    <div
      data-testid="subscription-status-badge"
      className={clsx(styles.cardLabelContainer, {
        'past-due': isWarning,
      })}
    >
      <div className={styles.cardLabelIcon}>{icon}</div>
      <div className={styles.cardLabel}>{label}</div>
    </div>
  );
};
